<template>
    <main class="main coin-bundle-page page-inside">
        <div class="main-wrapper">
            <section class="section hero-section">
            </section>
            <section class="section coin-bundle-section">
                <div class="wrapper">
                    <!-- Loading State -->
                    <div v-if="loading" class="loading-state">
                        <div class="loading-spinner"></div>
                        <div class="desc">{{ $t('Loading bundle...') }}</div>
                    </div>
                    
                    <!-- Error State -->
                    <div v-else-if="error" class="error-state">
                        <div class="error-message">
                            <div class="title">{{ $t('Error') }}</div>
                            <div class="desc">{{ error }}</div>
                            <button class="button" @click="fetchCoinBundle">
                                {{ $t('Try Again') }}
                            </button>
                        </div>
                    </div>
                    
                    <!-- Success State -->
                    <div v-else-if="coinBundle" class="coin-bundle-preview">
                            <div class="preview-container">
                                <div class="preview-wrapper">
                                    <div class="preview">
                                        <img :src="coinBundle.image" class="img" :alt="coinBundle.name" />
                                    </div>
                                </div>
                            </div>
                            <div class="text">
                                <div class="text-wrapper">
                                    <div class="title">{{ coinBundle.name }}</div>
                                    <div class="desc description">
                                        <b>{{ $t('Description:') }}</b>
                                        <div class="desc" v-html="coinBundle.content_desc"></div>
                                    </div>
                                    <div class="row-price">
                                        <div class="desc">
                                            <b>{{ $t('Price:') }}</b>
                                        </div>
                                        <div class="title">{{ coinBundle.price }} <span class="currency">{{ currency
                                                }}</span></div>
                                        <button class="button" @click="openTopUpModal" :disabled="purchasing">
                                            <span v-if="!purchasing">{{ $t('Buy Now') }}</span>
                                            <span v-else>{{ $t('Processing...') }}</span>
                                            <img v-if="!purchasing" src="./../img/cartSmall.svg" class="img" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                </div>
            </section>
        </div>
    </main>
</template>

<script>
import { defineComponent } from 'vue';

import { mapGetters } from "vuex";

export default defineComponent({
  emits: ['openTopUpModal'],
  name: 'CoinBundlePage',
  inject: ['isAuth', 'openSignInModal'],
  data() {
      return {
          coinBundle: null,
          loading: false,
          error: null,
          purchasing: false,
      };
  },

  computed: {
      ...mapGetters('app', ['currency']),
  },

  created() {
      this.fetchCoinBundle();
  },

  watch: {
      currency() {
          this.fetchCoinBundle();
      }
  },

  methods: {
      fetchCoinBundle() {
          const slug = this.$route.params.slug;
          this.loading = true;
          this.error = null;
          this.coinBundle = null;
          
          this.$http.get(`coin-bundles/${slug}?currency=${this.currency}`)
              .then((res) => {
                  if (res.status === 200 && res.data.payload) {
                      this.coinBundle = res.data.payload;
                  } else {
                      this.error = res.data.message || this.$t('Failed to load bundle');
                  }
              })
              .catch((error) => {
                  console.error(error);
                  if (error.response) {
                      this.error = error.response.data.message || this.$t('Failed to load bundle');
                  } else if (error.request) {
                      this.error = this.$t('Network error. Please check your connection.');
                  } else {
                      this.error = this.$t('An unexpected error occurred');
                  }
              })
              .finally(() => {
                  this.loading = false;
              });
      },
      openTopUpModal() {
          if (this.isAuth) {
              if (this.coinBundle && this.coinBundle.price) {
                  this.$emit('openTopUpModal', parseFloat(this.coinBundle.price), 'coinBundle');
              }
          } else {
            this.openSignInModal();
          }
      }
  },
});
</script>

<style scoped lang="scss">
.coin-bundle-page {
    .coin-bundle-preview {
        display: flex;
        width: 100%;
        margin: 0 auto;
        padding: 32px 0;

        .preview-container {
            width: 40%;
        }

        .preview-wrapper {
            display: flex;
            align-items: center;

            .title {
                width: 55%;
                text-align: left;
                font-size: 24px;
                line-height: 34px;
                font-weight: 600;
                padding-left: 50px;
                color: #fff;
            }
        }

        .preview {
            height: 100%;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 0;

            img {
                width: 100%;
            }
        }

        .text {
            width: 60%;
            padding-left: 40px;
            padding-top: 12px;
            display: flex;
            justify-content: center;
        }

        .text-wrapper {
            width: 100%;
        }

        .title {
            text-align: left;
        }

        .tag {
            margin-top: 24px;
            position: relative;

            .desc+.desc {
                margin-top: 10px;
            }
        }

        .feature-list {
            display: flex;
            flex-wrap: wrap;
            margin: 30px 0 0;
            gap: 16px 48px;
        }

        .description {
            text-align: left;
            margin-top: 24px;

            .desc+.desc {
                margin-top: 10px;
            }
        }

        .desc span,
        .desc {
            background: transparent !important;
            color: #02150C;
        }

        .feature-list__item+.feature-list__item {
            margin-top: 0;
        }

        .feature-list__item {
            color: #8D8D8D;
            display: flex;
            align-items: center;

            img {
                margin-left: 10px;
            }
        }

        .row {
            margin-top: 0;
            display: flex;
            align-items: center;

            +.row {
                margin-top: 24px;
            }
        }

        .row-price {
            margin-top: 24px;

            .title {
                font-size: 24px;
                line-height: normal;
                margin-top: 6px;
            }

            .button {
                margin-top: 18px;
            }
        }

        .spec-text {
            text-align: right;
            margin-top: 12px;

            .desc {
                color: #63726A;
                font-size: 12px;
                text-align: left;

                +.desc {
                    margin-top: 28px;
                }
            }
        }
    }

    .hero-section {
        padding-top: 158px;
        padding-left: 16px;
        padding-right: 16px;
        text-align: left;
    }

    .hover-link {
        color: #fff;
        display: inline-flex;
        gap: 24px;
        align-items: center;
    }

    .products {
        margin-top: 48px;
    }

    .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px 20px;
        text-align: center;

        .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
        }

        .desc {
            color: #666;
            font-size: 16px;
        }
    }

    .error-state {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 80px 20px;

        .error-message {
            text-align: center;
            max-width: 400px;

            .title {
                color: #dc3545;
                font-size: 24px;
                margin-bottom: 16px;
            }

            .desc {
                color: #666;
                margin-bottom: 24px;
                line-height: 1.5;
            }


        }
    }

    .button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
